import type { Definition } from "@/lib/search";

import type { CardSignal } from "./cardSignal";

export type BadgeTone = "neutral" | "known" | "elsewhere" | "gap";

export interface Badge {
  label: string;
  tone: BadgeTone;
  mono?: boolean;
  /**
   * This badge names something the frequency sort orders by, so it survives a COLLAPSED row.
   *
   * It replaces a style test — the collapsed filter used to be `mono || tone !== "neutral"`, which
   * asked what a badge *looks* like in order to answer what it *means*. That is why it had to
   * change: the markers below are neutral and not mono, so they would have been dropped from
   * exactly the rows 日本-499 is about, silently and with nothing to notice.
   *
   * `常用` deliberately does not carry it. It is not a sorting tag — see below.
   */
  dense?: boolean;
}

/**
 * How a sort marker is written on a badge (日本-499).
 *
 * **The stored strings are the server's; the words are ours.** These are Anki tags and values in
 * the `Extra` field, so translating them at the source would leave every existing note carrying
 * the old spelling — `surfaces/setup/jobText.ts` has the same shape for the same reason, and like
 * it this map **fails open**: an unknown marker renders as itself rather than vanishing.
 *
 * The numbers are JMdict's own and the words come off the `ke_pri` DTD comment in the vendored
 * `data/dictionary/JMdict_e`:
 *
 * * `ichi1`/`ichi2` — the 一万語 ("ten thousand words") list, which is where 一万語 comes from.
 * * `news1`/`news2` — the first and second 12,000 of a newspaper wordfreq file.
 * * `spec1`/`spec2` — "detected as being common, but not included in other lists". There is no
 *   established Japanese for that, so 特 is an abbreviation rather than a translation, chosen
 *   (Alex, 28 Aug 2026) over inventing a compound like 特常.
 */
export const MARKER_LABEL: Record<string, string> = {
  Ichimango1: "一万語1",
  Ichimango2: "一万語2",
  Newspaper1: "新聞1",
  Newspaper2: "新聞2",
  SpecialCommon1: "特1",
  SpecialCommon2: "特2",
};

/**
 * The badge row on a result card. Pure, so it is unit-testable — and worth testing, because the
 * state badge is what tells you at a glance whether a word is already yours.
 *
 * Order is rank → markers → JLPT → state: the evidence first, then what overrides it. Since
 * 日本-489 a commonness tag CAPS how badly a card sorts, so a rank on its own is not the answer to
 * "where will this land" and is very often not even close to it — measured over the whole
 * dictionary, 81.3% of the forms carrying a floor sort at the floor rather than at their rank, and
 * 23,181 of those have no rank at all. 直ぐ reads `#71,738` and sorts at 10,000.
 */
export function resultBadges(entry: Definition, signal: CardSignal): Badge[] {
  const badges: Badge[] = [];

  if (entry.frequency !== null) {
    badges.push({
      label: `#${entry.frequency.toLocaleString()}`,
      tone: "neutral",
      mono: true,
      dense: true,
    });
  }

  // **`常用` is drawn only when there is no marker to draw.** It is true for anything JMdict marks
  // with ANY priority, the nfXX bands included — a much looser "common" than it looks — so beside
  // a marker it is a badge of pure duplication, which is 93.3% of the rows a marker is on. For the
  // other 6.7% (nf or gai bands only) it is the one signal there is, which is why it stays.
  //
  // It gets no `dense`: it names no tag, so nothing about it survives into a collapsed row, which
  // is what it already did before this.
  //
  // **Read defensively, because this list arrives from STORED payloads too.** Ask keeps a `dict`
  // result's entries verbatim in `ask_message.content.payload` and re-renders them with the real
  // `ResultCard` — and `_loaded` rebuilds `present_definitions`' shelf out of those same rows — so
  // a thread written before this field existed hands us an entry with no `sortMarkers` at all.
  // Unguarded that is a `TypeError` on the transcript's own render, not a missing badge.
  const markers = entry.sortMarkers ?? [];
  if (markers.length > 0) {
    for (const marker of markers) {
      badges.push({ label: MARKER_LABEL[marker] ?? marker, tone: "neutral", dense: true });
    }
  } else if (entry.isCommon) {
    badges.push({ label: "常用", tone: "neutral" });
  }

  // JLPT is a sorting tag too — `JLPT_N5` floors at 2,000, tighter than any of the markers above.
  // It used to be dropped from collapsed rows, which put すぐに in the position of showing 特1
  // (10,000) while hiding the N5 that actually decides where it goes: the weaker signal drawn, the
  // stronger one hidden, on one row.
  if (entry.jlptLevel !== null) {
    badges.push({ label: `JLPT N${entry.jlptLevel}`, tone: "neutral", dense: true });
  }

  // Three states, because the underline under the same word has three. `elsewhere` names the card
  // that covers it — the row is offering to make a SECOND card for a word you already hold under
  // another spelling, and saying which is the difference between an informed press and a surprise.
  // `others` is the rest of them: the cactus sits on サボテン and 仙人掌 at once.
  if (signal.state === "known") {
    badges.push({ label: "登録済", tone: "known" });
  } else if (signal.state === "elsewhere" && signal.cardedAs) {
    // The term is user content — a headword out of the collection — so the verb wraps it and it is
    // never itself translated.
    const { term, others } = signal.cardedAs;
    badges.push({
      label: others > 0 ? `${term} +${others}で登録済` : `${term}で登録済`,
      tone: "elsewhere",
    });
  } else {
    badges.push({ label: "未登録", tone: "gap" });
  }

  return badges;
}

/**
 * The design's only density rule: at **nine or more** rows for one word, every result collapses to
 * a single line and the focused one expands. Below nine, all results render expanded with ALL
 * senses — a 17-sense する simply runs long and the list scrolls. Nothing is ever behind a
 * "show more".
 *
 * It was five while a row meant a dictionary ENTRY. 日本-290 made a row an (entry × written form)
 * pair, so the same lookups produce roughly 1.7× the rows and five would have collapsed 19% of
 * lookups against today's 7%. Nine, from two derivations that agree: it keeps the share of
 * collapsed lookups at 7.7% against the old rule's 7.3%, and the lookups sitting exactly at the
 * old five-entry boundary produce a median of nine rows under the new one.
 *
 * What does NOT hold constant is sense volume — a nine-row lookup carries roughly twice the senses
 * a five-entry one did, because several rows are now one entry under different spellings and
 * repeat its senses. Row count was always the rule's basis; if the dock starts feeling long before
 * it collapses, that is the number to revisit, not this one.
 */
export const DENSITY_THRESHOLD = 9;

export function shouldCollapse(rowCount: number): boolean {
  return rowCount >= DENSITY_THRESHOLD;
}
